import {useContext, useMemo} from 'react';
import {tokenize, rankEntries, splitHighlight, prepareEntry, buildSnippet} from '@embertracking/common/data/search/matcher.mjs';
import {searchTypeLabel} from '@embertracking/common/data/search/types.mjs';
import {REPORT_CATALOG, canViewReport} from '@embertracking/common/data/reports/registry.mjs';

import useMenuItems from '-/components/navigation/useMenuItems';
import {SETTINGS_SECTIONS, sectionPath} from '-/pages/settings/sections';
import {UserContext} from '-/contexts/User';
import {SettingsContext} from '-/contexts/Settings';
import {hasPermission} from '-/data/utils';
import {hasFeature} from '-/features';
import {MODULES, VISIBLE_MODULES, getPublishedLibrary, inCategory, articlePlainText} from '-/pages/help/helpContent';

// The client-side half of global search: destinations the app already holds in
// memory — nav items, the report catalog, the settings rail.
//
// None of this touches the backend. Every catalog here is built by a hook that
// is ALREADY permission- and feature-filtered (that's what makes the sidebar,
// the reports page and the settings rail correct), so matching against it can't
// leak a destination the member can't reach — and it renders on the first
// keystroke, while the api request for the record types is still in flight.
//
// #1486 is the neighbouring bug this half fixes: the settings rail's own search
// only indexes what its cards render, so a heading like "RDCK" finds nothing.
// Indexing `blurb` and `keywords` here means the ⌘K field answers it.

const GROUP_LIMIT = 4;

// Help is a bundled, shared corpus: build it once, without any usage queries.
// Match only articles that the Help page can actually open.
const helpEntries = getPublishedLibrary({flags: {}, counts: {}}, new Set()).map(entry => prepareEntry({
    type: 'help',
    id: entry.id,
    url: `/help?article=${encodeURIComponent(entry.id)}`,
    title: entry.title,
    subtitle: MODULES[VISIBLE_MODULES.find(id => inCategory(entry, id))].label,
    typeLabel: searchTypeLabel('help'),
    fields: [['', articlePlainText(entry), '']],
    date: null
}));

// A settings section is findable by its label, the blurb under it, and its
// `keywords` aliases — the ones that never render but are what someone actually
// types ("acronym" for the row labelled "Names").
const settingsEntry = section => prepareEntry({
    type: 'settings',
    id: `settings-${section.id}`,
    url: sectionPath(section.id),
    title: section.label,
    subtitle: 'Settings',
    typeLabel: searchTypeLabel('settings'),
    fields: [
        ['Description', section.blurb || '', ''],
        // Aliases are searchable but deliberately not shown as a snippet label —
        // they're a matching aid, not something to read back.
        ['Also known as', (section.keywords || []).join(', '), '']
    ],
    date: null
});

const reportEntry = report => prepareEntry({
    type: 'reports',
    id: `report-${report.id}`,
    url: `/reports/${report.id}`,
    title: report.title,
    // A report that isn't built yet is still worth finding — it tells you the
    // answer is coming — but it says so rather than pretending to be a link.
    subtitle: report.status === 'available' ? 'Report' : 'Report · coming soon',
    typeLabel: searchTypeLabel('reports'),
    fields: [['Answers', report.question || '', '']],
    date: null
});

const pageEntry = item => prepareEntry({
    type: 'pages',
    id: `page-${item.id}`,
    url: item.to,
    title: item.label,
    subtitle: 'Page',
    typeLabel: searchTypeLabel('pages'),
    fields: [['Also known as', (item.keywords || []).join(', '), '']],
    date: null
});

const toGroup = (type, entries, tokens, limit) => {
    const ranked = rankEntries(entries, tokens, {typeOrder: [type]});
    if (!ranked.length) return null;
    return {
        type,
        label: searchTypeLabel(type),
        count: Math.min(ranked.length, limit),
        total: ranked.length,
        items: ranked.slice(0, limit).map(entry => ({
            type: entry.type,
            id: entry.id,
            url: entry.url,
            title: entry.title,
            subtitle: entry.subtitle,
            date: null,
            status: null,
            score: entry.score,
            highlight: splitHighlight(entry.title, tokens),
            snippet: buildSnippet(entry, tokens)
        }))
    };
};

export default function useLocalResults(query, {limit = GROUP_LIMIT} = {}) {
    const menuItems = useMenuItems();
    const {currentUser} = useContext(UserContext);
    // Settings drives hasFeature(); depend on it so toggling a feature
    // re-matches live, the same reason useMenuItems() does.
    const settings = useContext(SettingsContext);

    const entries = useMemo(() => {
        const isAdmin = hasPermission(currentUser, 'admin');
        const canRead = permission => hasPermission(currentUser, permission);

        return {
            pages: menuItems.map(pageEntry),
            // The whole Settings area is admin-only (Router.js), and each section
            // carries its own gate on top.
            settings: isAdmin
                ? SETTINGS_SECTIONS
                    .filter(section => !section.gate || section.gate({currentUser, hasFeature, hasChangelog: true}))
                    .map(settingsEntry)
                : [],
            reports: canRead('reports.read')
                ? REPORT_CATALOG.filter(report => canViewReport(report, canRead)).map(reportEntry)
                : []
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [menuItems, currentUser, settings]);

    return useMemo(() => {
        const tokens = tokenize(query);
        if (!tokens.length) return [];

        return [
            toGroup('reports', entries.reports, tokens, limit),
            toGroup('settings', entries.settings, tokens, limit),
            toGroup('pages', entries.pages, tokens, limit),
            toGroup('help', helpEntries, tokens, limit)
        ].filter(Boolean);
    }, [entries, query, limit]);
}
